import type { BookingMode, PaymentStatus } from './provider';

export type { BookingMode, PaymentStatus } from './provider';

// ─── CancellationReason ──────────────────────────────────────────────────────

export const CANCELLATION_REASONS = [
  'PLANS_CHANGED',
  'NEED_DIFFERENT_TIME',
  'CANNOT_ATTEND',
  'OTHER',
] as const;

export type CancellationReason = (typeof CANCELLATION_REASONS)[number];

// ─── ConfirmationModel ───────────────────────────────────────────────────────

export type ConfirmationModel = 'INSTANT' | 'APPROVAL_REQUIRED';

// ─── Business ────────────────────────────────────────────────────────────────

export interface Business {
  id: number;
  name: string;
  description: string | null;
  logoUrl: string | null;
  address: string | null;
  phone: string | null;
  workingHoursNote: string | null;
  isOpen: boolean;
  supportedLanguages?: string[];
  defaultLanguage?: string;
  _lang?: string;
  _fallback?: boolean;
}

// ─── ClientProvider ──────────────────────────────────────────────────────────

export interface ClientProvider {
  id: number;
  displayName: string;
  bio: string | null;
  photoUrl: string | null;
  active: boolean;
  defaultBookingMode: BookingMode;
  _lang?: string;
  _fallback?: boolean;
}

// ─── ClientService ───────────────────────────────────────────────────────────

export interface ClientService {
  id: number;
  name: string;
  description: string | null;
  durationMinutes: number;
  price: number;
  confirmationModel: ConfirmationModel;
  active: boolean;
  _lang?: string;
  _fallback?: boolean;
}

// ─── ClientBookingStatus ─────────────────────────────────────────────────────

export type ClientBookingStatus =
  | 'PENDING'
  | 'CONFIRMED'
  | 'CANCELLED'
  | 'COMPLETED'
  | 'EXPIRED'
  | 'REFUND_REQUESTED'
  | 'REFUND_COMPLETED'
  | 'REJECTED';

export const ACTIVE_BOOKING_STATUSES: ClientBookingStatus[] = [
  'PENDING',
  'CONFIRMED',
];

export const HISTORY_BOOKING_STATUSES: ClientBookingStatus[] = [
  'CANCELLED',
  'COMPLETED',
  'EXPIRED',
  'REFUND_REQUESTED',
  'REFUND_COMPLETED',
  'REJECTED',
];

// ─── ClientBooking ───────────────────────────────────────────────────────────

export interface ClientBooking {
  id: number;
  bookingUuid: string;
  providerId: number;        // backend: serviceProviderId (mapped in API layer)
  serviceId: number;
  providerName: string | null;        // list endpointda yo'q bo'lishi mumkin
  serviceName: string | null;         // list endpointda yo'q bo'lishi mumkin
  durationMinutes: number | null;     // list endpointda yo'q bo'lishi mumkin
  startTime: string; // ISO
  endTime: string; // ISO
  status: ClientBookingStatus;
  confirmationModel: ConfirmationModel; // backend: confirmationModel
  totalAmount: number | null;
  customerNotes: string | null;
  cancellationReason: string | null;
  createdAt: string; // ISO
  cancellationDeadline: string | null; // ISO — details endpointdan keladi
  paymentStatus: PaymentStatus | null;
}

// ─── ClientBookingDetails ────────────────────────────────────────────────────

export interface ClientBookingDetails {
  id: number;
  bookingUuid: string;
  date: string;                        // YYYY-MM-DD
  time: string;                        // HH:mm
  serviceId: number;
  serviceName: string;
  providerId: number;
  providerName: string;
  durationMinutes: number;
  price: number;
  paymentLabel: string;
  status: ClientBookingStatus;
  canRequestCancel: boolean;
  cancellationDeadlinePassed: boolean;
  note: string | null;
  paymentStatus: PaymentStatus | null;
}

// ─── CreateBookingReq ────────────────────────────────────────────────────────

export interface CreateBookingReq {
  providerId: number;
  serviceId: number;
  date: string;   // YYYY-MM-DD
  time: string;   // HH:mm
  customerNotes?: string;
}

// ─── CreateBookingRes ────────────────────────────────────────────────────────

export interface CreateBookingRes {
  bookingId: number;
  confirmationModel: ConfirmationModel;
  expiresAt: string | null; // ISO, null if INSTANT
}

// ─── ErrorKind ───────────────────────────────────────────────────────────────

export type ErrorKind = 'network' | 'offline' | 'conflict' | 'deadline' | 'unknown';

// ─── SmartHint ───────────────────────────────────────────────────────────────

export interface SmartHint {
  nearestBookingAt: string | null; // ISO
  pendingCount: number;
  cancellationRequestCount: number;
}

// ─── ClientBookingItem ───────────────────────────────────────────────────────

export interface ClientBookingItem {
  id: number;
  bookingUuid: string;
  date: string; // YYYY-MM-DD
  time: string; // HH:mm
  serviceId: number;
  serviceName: string;
  providerId: number;
  providerName: string;
  durationMinutes: number;
  price: number;
  paymentLabel: string;
  status: ClientBookingStatus;
  canRequestCancel: boolean;
  cancellationDeadlinePassed: boolean;
  note: string | null;
  paymentStatus: PaymentStatus | null;
}

// ─── BookingListResponse ──────────────────────────────────────────────────────

export interface BookingListResponse {
  items: ClientBookingItem[];
  hint: SmartHint | null;
  nextCursor: string | null;
}

// ─── CancelBookingReq ────────────────────────────────────────────────────────

export interface CancelBookingReq {
  reason: CancellationReason;
}

// ─── CancelBookingResponse ───────────────────────────────────────────────────

export interface CancelBookingResponse {
  success: true;
  data: null;
}

// ─── ClientCancelErrorKind ───────────────────────────────────────────────────

export type ClientCancelErrorKind = 'deadline' | 'network' | 'offline' | 'unknown';
